"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getEligibilityCheckByReferralId,
  getEligibilityCheckByTaskId,
  verifyInsuranceEligibilityAtIntake,
  type VerifyInsuranceEligibilityRequestPayload,
} from "@/src/requests/stedi-eligibility-check";

export const STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "stedi-eligibility-check";
export const INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "intake-stedi-eligibility-check";

export function useStediEligibilityCheck(referralId: string | null) {
  const query = useQuery({
    queryKey: [STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId],
    queryFn: () => {
      if (!referralId) return null;

      return getEligibilityCheckByReferralId(referralId);
    },
    enabled: !!referralId,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useIntakeStediEligibilityCheck(taskId: string | null) {
  const query = useQuery({
    queryKey: [INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId],
    queryFn: () => {
      if (!taskId) return null;

      return getEligibilityCheckByTaskId(taskId);
    },
    enabled: !!taskId,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useVerifyInsuranceEligibilityAtIntake(taskId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VerifyInsuranceEligibilityRequestPayload) => {
      if (!taskId) {
        throw new Error("Task id is required to verify insurance eligibility");
      }

      return verifyInsuranceEligibilityAtIntake(taskId, payload);
    },
    onSuccess: (data) => {
      if (!taskId) return;

      queryClient.setQueryData([INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId], data.check);
    },
  });
}
